import
{
    useState,
    useEffect,
    useContext,
    createContext
} from 'react';

import axios from 'axios';
import useSWR from 'swr';

const Context = createContext(null);

// 1. Creamos una variable en memoria fuera del componente.
// Es síncrona y cambia de inmediato, sin esperar a React.
let tokenEnMemoria = localStorage.getItem('auth-token') || null;

export const api = axios.create(
{
    baseURL: process.env.REACT_APP_API_URL,
    headers:
    {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }
});

// 2. El interceptor ahora es ultra seguro: revisa la memoria primero
api.interceptors.request.use
(
    (config) =>
    {
        // Prioridad 1: Variable en memoria (para peticiones instantáneas post-login)
        // Prioridad 2: LocalStorage (por si se recargó la página)
        const tokenValido = tokenEnMemoria || localStorage.getItem('auth-token');

        if (tokenValido) { config.headers['Authorization'] = `Bearer ${tokenValido}`; }

        return config;
    },
    (error) => Promise.reject(error)
);

// Este interceptor interceptará CUALQUIER respuesta 401 de tu backend globalmente
export const configurarInterceptorRespuestas = (logout) =>
{
    api.interceptors.response.use
    (
        // Si la respuesta es exitosa (200, 201), pasa directo
        (response) => response,
        (error) =>
        {
            // Si el backend arroja 401 Unauthorized
            if (error.response && error.response.status === 401)
            {
                // Ejecuta el borrado de estados y localStorage
                logout();
            }

            // Lanza el error para que rompa el flujo de la función
            return Promise.reject(error);
        }
    );
};

// Definimos el fetcher usando tu instancia de axios configurada
const fetcher = (url) => api.get(url).then(response => response.data);

export const AuthProvider = ({ children }) =>
{
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [resend, setResend] = useState(false);
    const [token, setToken] = useState(localStorage.getItem('auth-token') || null);

    // 3. Modificamos la función setToken envolviéndola para capturar el valor al instante
    const actualizarToken = (nuevoToken) =>
    {
        tokenEnMemoria = nuevoToken;

        if (nuevoToken)
        {
            localStorage.setItem('auth-token', nuevoToken);
        }
        else
        {
            localStorage.removeItem('auth-token');
        }

        // Sigue actualizando el estado de React para tus componentes
        setToken(nuevoToken);
    };

    const logout = () =>
    {
        tokenEnMemoria = null;

        setEmail('');
        setPassword('');
        setToken(null);

        localStorage.removeItem('auth-token');
    };

    // Ajuste en useEffect: El interceptor se configura una sola vez al montar
    useEffect(() =>
    {
        configurarInterceptorRespuestas(logout);
    }, []);

    // Configuración de SWR para validar el token cada 30 segundos
    useSWR(
        token ? `${process.env.REACT_APP_API_URL}/usuarios-buscadores/token` : null,
        fetcher,
        {
            // 30 segundos
            refreshInterval: 30000,

            // Revalida si el usuario cambia de pestaña y vuelve
            revalidateOnFocus: true,

            // Revalida si recupera conexión a internet
            revalidateOnReconnect: true,

            // Si falla con 401, no queremos que intente de nuevo infinitamente
            shouldRetryOnError: false,
        }
    );

    return (
        <Context.Provider
            value={
            {
                api,
                email,
                setEmail,
                password,
                setPassword,
                message,
                setMessage,
                resend,
                setResend,
                token,
                setToken: actualizarToken,
                logout,
            }}
        >
            { children }
        </Context.Provider>
    );
}

export const useAuth = () => useContext(Context);
